import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../config';

const Login: React.FC = () => {
  const { login, isAuthenticated, loading, user } = useAuth();
  const navigate = useNavigate();
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRedirectingRef = useRef(false); // Track xem đang redirect chưa
  
  // Sử dụng sessionStorage để track redirect (persist qua remount)
  const REDIRECT_FLAG_KEY = '__login_redirect_flag__';
  const REDIRECT_TIMESTAMP_KEY = '__login_redirect_timestamp__';
  const REDIRECT_TIMEOUT = 5000; // 5 giây

  useEffect(() => {
    // Kiểm tra flag redirect TRƯỚC TIÊN để tránh xử lý không cần thiết
    const hasRedirected = sessionStorage.getItem(REDIRECT_FLAG_KEY) === 'true';
    const redirectTimestamp = sessionStorage.getItem(REDIRECT_TIMESTAMP_KEY);
    const now = Date.now();
    
    // Nếu đã redirect gần đây (< 500ms), bỏ qua hoàn toàn
    if (hasRedirected && redirectTimestamp) {
      const timeSinceRedirect = now - parseInt(redirectTimestamp, 10);
      if (timeSinceRedirect < 500) {
        // Không log gì cả để tránh spam console
        return;
      } else {
        // Đã quá timeout, clear flag
        sessionStorage.removeItem(REDIRECT_FLAG_KEY);
        sessionStorage.removeItem(REDIRECT_TIMESTAMP_KEY);
      }
    }
    
    // Nếu đang redirect (ref), không làm gì cả
    if (isRedirectingRef.current) {
      return;
    }
    
    // Kiểm tra token trong localStorage trước
    const tokenInStorage = localStorage.getItem('access_token');
    
    // QUAN TRỌNG: Nếu đã authenticated, redirect về dashboard hoặc tuvan_nhathau (nếu đơn vị là tư vấn/nhà thầu và user có tuvan_nhathau === 1)
    if (isAuthenticated && user) {
      // Clear redirect flag khi authenticated thành công
      sessionStorage.removeItem(REDIRECT_FLAG_KEY);
      sessionStorage.removeItem(REDIRECT_TIMESTAMP_KEY);
      isRedirectingRef.current = true;
      const target = (user.donvi_type === 2 && user.tuvan_nhathau === 1) ? '/tuvan_nhathau' : '/dashboard';
      navigate(target, { replace: true });
      return;
    }
    
    // Nếu đang loading, đợi xong rồi mới quyết định
    if (loading) {
      return;
    }
    
    // Nếu có token nhưng chưa authenticated, có thể đang trong quá trình checkAuth
    if (tokenInStorage && !isAuthenticated) {
      // Nếu đã redirect gần đây (trong vòng REDIRECT_TIMEOUT), không redirect lại
      if (hasRedirected && redirectTimestamp) {
        const timeSinceRedirect = now - parseInt(redirectTimestamp, 10);
        if (timeSinceRedirect < REDIRECT_TIMEOUT) {
          return;
        }
      }
      
      // Clear timeout cũ nếu có
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
      
      // Đợi một chút để checkAuth hoàn thành
      redirectTimeoutRef.current = setTimeout(() => {
        // Kiểm tra lại state trước khi redirect
        const currentToken = localStorage.getItem('access_token');
        const stillNotAuthenticated = !isAuthenticated && !loading;
        const hasRedirectedRecently = sessionStorage.getItem(REDIRECT_FLAG_KEY) === 'true';
        
        if (stillNotAuthenticated && currentToken && !hasRedirectedRecently && !isRedirectingRef.current) {
          // Đánh dấu đã redirect để tránh loop
          isRedirectingRef.current = true;
          sessionStorage.setItem(REDIRECT_FLAG_KEY, 'true');
          sessionStorage.setItem(REDIRECT_TIMESTAMP_KEY, Date.now().toString());
          
          const loginUrl = `${API_URL}/auth/login`;
          window.location.replace(loginUrl);
        }
      }, 2000); // Đợi 2 giây
      
      return () => {
        if (redirectTimeoutRef.current) {
          clearTimeout(redirectTimeoutRef.current);
        }
      };
    }
    
    // Nếu không có token và không authenticated, redirect về login API NGAY
    if (!tokenInStorage && !isAuthenticated && !loading) {
      // Đánh dấu đã redirect TRƯỚC KHI redirect để tránh re-render xử lý lại
      isRedirectingRef.current = true;
      sessionStorage.setItem(REDIRECT_FLAG_KEY, 'true');
      sessionStorage.setItem(REDIRECT_TIMESTAMP_KEY, Date.now().toString());
      
      const loginUrl = `${API_URL}/auth/login`;
      // Sử dụng window.location.replace thay vì href để tránh re-render
      window.location.replace(loginUrl);
      return; // Return ngay sau khi redirect
    }
  }, [login, isAuthenticated, loading, user, navigate]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{ gap: 2 }}
    >
      <CircularProgress size={60} />
      <Typography variant="h6">
        Đang chuyển hướng đến trang đăng nhập...
      </Typography>
    </Box>
  );
};

export default Login;

